import type { ConnectedDevice } from "@rakazo/contracts";
import { Button, Input, NativeSelect, NativeSelectOption } from "@rakazo/ui-web";
import { Monitor, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { rpc } from "../lib/rpc";

function useDevices() {
  const [devices, setDevices] = useState<ConnectedDevice[]>([]);
  const [error, setError] = useState("");
  const refresh = () =>
    rpc.devices
      .list()
      .then(setDevices)
      .catch(() => setError("Could not load devices"));
  useEffect(() => {
    void refresh();
    const timer = setInterval(refresh, 10_000);
    return () => clearInterval(timer);
  }, []);
  return { devices, error, setError, refresh };
}
export function ConnectedDevices() {
  const { devices, error, setError, refresh } = useDevices();
  const [name, setName] = useState("");
  const [localId, setLocalId] = useState<string | null>(null);
  useEffect(() => {
    void window.rakazoDesktop?.devices?.status().then((state) => setLocalId(state?.id ?? null));
  }, []);
  const [busy, setBusy] = useState(false);
  const [screenReady, setScreenReady] = useState(false);
  async function act(work: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await work();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update device");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 data-devices-heading className="text-[15px] font-medium">
          Devices
        </h3>
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label="Refresh devices"
          onClick={() => void refresh()}
        >
          <RefreshCw className="size-4" />
        </Button>
      </div>
      {devices.map((device) => (
        <DeviceRow key={device.id} device={device} busy={busy} act={act} />
      ))}
      {localId && devices.some((d) => d.id === localId) ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">This computer is connected.</p>
          {window.rakazoDesktop?.devices?.enableScreen ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  setScreenReady(false);
                  const result = await window.rakazoDesktop!.devices!.enableScreen!();
                  if (!result.screen)
                    throw new Error(
                      "Allow Screen Recording for this app in System Settings, then quit and reopen it.",
                    );
                  if (!result.accessibility)
                    throw new Error(
                      "Allow Accessibility for this app in System Settings, then quit and reopen it.",
                    );
                  if (result.capture === false)
                    throw new Error(
                      "Screen capture is unavailable. Wake and unlock this Mac, then check again.",
                    );
                  setScreenReady(true);
                })
              }
            >
              {screenReady ? "Screen control ready" : "Check screen control"}
            </Button>
          ) : null}
        </div>
      ) : window.rakazoDesktop?.devices ? (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void act(async () => {
              const pair = await rpc.devices.pair({ name: name.trim() || "My computer" });
              const result = await window.rakazoDesktop!.devices!.pair(pair.code);
              if (result) {
                setName("");
                setLocalId(result.id);
              }
            });
          }}
        >
          <Input
            aria-label="Computer name"
            placeholder="Computer name"
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" disabled={busy}>
            Connect this computer
          </Button>
        </form>
      ) : (
        <p className="text-xs text-muted-foreground">Connect a computer from its desktop app.</p>
      )}
      <p className="text-xs text-muted-foreground">Select a device in a bot’s Computer setting.</p>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
function DeviceRow({
  device,
  busy,
  act,
}: {
  device: ConnectedDevice;
  busy: boolean;
  act: (work: () => Promise<unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(device.name);
  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Monitor className="size-4 shrink-0" />
        <Input
          aria-label={`Name for ${device.name}`}
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          className="h-8"
        />
        <Button
          size="sm"
          variant="ghost"
          disabled={busy || !name.trim() || name === device.name}
          onClick={() => void act(() => rpc.devices.rename({ id: device.id, name }))}
        >
          Save
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            if (
              window.confirm(
                `Disconnect ${device.name}? Bots assigned here will stay offline until you choose another computer.`,
              )
            )
              void act(() => rpc.devices.revoke({ id: device.id }));
          }}
        >
          Disconnect
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {device.online ? "Online" : device.paired ? "Offline" : "Awaiting connection"}
        {device.platform ? ` · ${device.platform === "darwin" ? "macOS" : device.platform}` : ""}
        {!device.online && device.lastSeenAt
          ? ` · Last seen ${new Date(device.lastSeenAt).toLocaleString()}`
          : ""}
      </p>
      {device.agents.length ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Installed agents</summary>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3">
            {device.agents.map((agent) => (
              <div key={agent.id} className="flex justify-between gap-2">
                <span>{agent.name}</span>
                <span className="text-muted-foreground">
                  {agent.installed ? "Detected" : "Not installed"}
                </span>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}
export function BotDevicePicker({ botId, deviceId }: { botId: string; deviceId?: string | null }) {
  const { devices, error, setError } = useDevices();
  const [selected, setSelected] = useState(deviceId ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSelected(deviceId ?? "");
  }, [botId, deviceId]);
  return (
    <div className="mb-5 space-y-2">
      <label htmlFor={`computer-${botId}`} className="text-xs font-medium text-muted-foreground">
        Computer
      </label>
      <NativeSelect
        id={`computer-${botId}`}
        value={selected}
        disabled={busy}
        onChange={(e) => {
          const next = e.target.value;
          setBusy(true);
          setError("");
          void rpc.devices
            .select({ botId, deviceId: next || null })
            .then(() => setSelected(next))
            .catch((e) => setError(e.message ?? "Could not switch computer"))
            .finally(() => setBusy(false));
        }}
      >
        <NativeSelectOption value="">Cloud (default)</NativeSelectOption>
        {selected && !devices.some((d) => d.id === selected) ? (
          <NativeSelectOption value={selected}>Disconnected computer</NativeSelectOption>
        ) : null}
        {devices
          .filter((d) => d.paired)
          .map((d) => (
            <NativeSelectOption key={d.id} value={d.id}>
              {d.name} · {d.online ? "Online" : "Offline"}
            </NativeSelectOption>
          ))}
      </NativeSelect>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
